'use client';

import { IconAperture } from '@/components/icons/IconAperture';
import { cn } from '@/lib/utils/tw-merge';
import { useTheme } from './useTheme';
import { toggleTheme } from './theme.store';

type ApertureToggleProps = {
    className?: string;
    iconClassName?: string;
    /** Hide the f-stop label below a very narrow viewport (the header does). */
    labelClassName?: string;
};

/**
 * The design's mode switch. The label names the mode you would switch TO, the
 * icon shows the mode you are IN — that pairing is deliberate: the aperture is
 * the current state, the f-stop is the destination.
 */
export function ApertureToggle({ className, iconClassName, labelClassName }: ApertureToggleProps) {
    const theme = useTheme();
    const isDark = theme === 'dark';

    return (
        <button
            type="button"
            onClick={() => toggleTheme()}
            aria-label={isDark ? 'Auf f/1,4 wechseln (Light Mode)' : 'Auf f/22 wechseln (Dark Mode)'}
            aria-pressed={!isDark}
            className={cn(
                'inline-flex cursor-pointer items-center gap-2 py-1',
                'text-[13px] font-semibold tracking-[0.04em]',
                'text-(--text-title) opacity-72 transition-opacity duration-200',
                'hover:opacity-100 active:scale-95',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-(--focus-ring-default)',
                className,
            )}
        >
            <IconAperture open={isDark} className={cn('block size-6', iconClassName)} />
            <span className={labelClassName}>{isDark ? 'f/1,4' : 'f/22'}</span>
        </button>
    );
}
